import React, { useRef, useState, useMemo, useEffect } from "react";
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Chip, Skeleton,
  ButtonBase, LinearProgress,
  InputAdornment, Select, MenuItem,
  Drawer, Divider, Stack, CircularProgress,
} from "@mui/material";
// The app's real notification system (see AiSettings.jsx's own toast
// import for the full writeup) — the upload dialog's errors were a plain
// inline Alert instead (Rahim, 2026-09-04 audit).
import { toast } from "react-hot-toast";
// Iconsax, matching the rest of this module's icon set (see
// icon-library-iconsax memory) — replaces the old plain MUI icons.
import {
  DocumentUpload, DocumentText, SearchNormal1, Trash, ExportSquare, AddCircle, Cpu,
} from "iconsax-react";
// Plain MUI Close (simple "X", no circle) — the actual established close-
// button icon everywhere else in the app (Inbox's ChatInfo/TextBox/
// ClusterDenMediaDialog, this same module's AgentConfigView/
// AgentsGetStartedPanel), not an iconsax circled variant.
import CloseIcon from "@mui/icons-material/Close";
import { getAuthToken } from "../../../../../utils/getAuthToken";
import { SERVER_DOMAIN, SOURCE_TYPE_LABELS, ACCEPTED_MIME } from "../constants";
import {
  LabeledField, inboxInputSx, inboxSelectSx, inboxSelectMenuProps, inboxMenuItemSx, skeletonSx,
} from "../components/LabeledField";
import ExpandableParagraphField from "../components/ExpandableParagraphField";

// Font sizing/weight, button, and icon treatment brought in line with the
// rest of the AI Settings rebuild (Config/Actions/Contact Filter/Test
// Agent — see ai-agents-wizard-redesign-2026-09-03 memory) instead of this
// tab's old ad-hoc MUI defaults (h6/body2/caption variants, plain
// Article/UploadFile icons, a solid green "contained" Add button).

// Neutral bordered button — never a solid/green fill ("never ever do a
// green button", the standing rule for every button on this page). Same
// formula duplicated in AgentsTab.jsx/AgentConfigView.jsx rather than a
// shared import — see the circular-import-avoidance pattern in ui-gotchas
// memory.
const neutralButtonSx = {
  borderRadius: "8px",
  backgroundColor: (theme) => theme.palette.background.default,
  borderColor: (theme) => theme.palette.divider,
  "&:hover": {
    backgroundColor: (theme) => theme.palette.action.hover,
    borderColor: (theme) => theme.palette.divider,
  },
  color: (theme) => theme.palette.text.primary,
  textTransform: "none",
  fontWeight: 600,
};

// Same "segmented filter type" selected-state formula as
// ContactFilterSection.jsx's own filterType row (green fill when active) —
// replaces the plain MUI ToggleButtonGroup's default blue-tinted selected
// state for the File/FAQ mode picker below.
const segmentedOptionSx = (active) => ({
  borderRadius: "8px",
  border: "1px solid",
  borderColor: (theme) => (active ? theme.palette.primary.main : theme.palette.divider),
  bgcolor: (theme) => (active ? theme.palette.primary.main : theme.palette.background.default),
  color: (theme) => (active ? theme.palette.primary.contrastText : theme.palette.text.secondary),
  px: 1.75, py: 0.875,
  fontSize: "0.8125rem",
  fontWeight: active ? 600 : 500,
  transition: "background-color 0.15s, border-color 0.15s",
  "&:hover": {
    bgcolor: (theme) => (active ? theme.palette.primary.dark : theme.palette.action.hover),
  },
});

// Iconsax icons don't accept the `sx` prop — every icon on this page is
// wrapped in a fixed-size Box instead (see icon-library-iconsax memory).
const ixBox = (size) => ({
  width: size, height: size, flexShrink: 0,
  display: "flex", alignItems: "center", justifyContent: "center",
});

// For the picked-file card's size line (Rahim, 2026-09-04: the attached
// file needs to actually read as attached — filename alone read the same
// as the empty state).
function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const cellSx = {
  border: 0,
  borderBottom: "1px solid",
  borderColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"),
  py: 1.4,
};

// Same chip formula AgentsTab.jsx's own Active/Inactive/Default chips use
// (height:22, 0.75rem/600, outlined), plus a tighter borderRadius (Rahim,
// 2026-09-04: "for active inactive... not too much corner radius... no
// need to add the icon for it tho") — button-shaped badge instead of MUI
// Chip's default pill radius, no icon.
const CHIP_SX = { height: 22, fontSize: "0.75rem", fontWeight: 600, borderRadius: "6px" };

function StatusChip({ status }) {
  if (status === "processing") return <Chip label="Processing" size="small" color="warning" variant="outlined" sx={CHIP_SX} />;
  if (status === "failed") return <Chip label="Failed" size="small" color="error" variant="outlined" sx={CHIP_SX} />;
  return <Chip label="Active" size="small" color="success" variant="outlined" sx={CHIP_SX} />;
}

// Assigned-agent tags (Rahim, 2026-09-04: "for agents dont use pill use
// button type corner radius" → "i dont like the icon also no need to add
// the background colour" → "use some other icon for the agents pill") —
// plain outlined, no-fill Box instead of MUI Chip (same reasoning
// ContactFilterSection.jsx's own `labelButtonSx` used to drop its label
// pills for small squared buttons), icon swapped from AiFaceIcon (the
// cartoonish robot-face glyph Rahim didn't like here) to `Cpu` — the same
// icon the LeftNav rail already uses for the "AI" nav item itself, per
// icon-library-iconsax memory, so it reads as a plain neutral AI mark
// instead of a mascot. `icon=false` for the "+N" overflow tag, which
// doesn't represent one specific agent.
function AgentTag({ label, icon = true }) {
  return (
    <Box
      sx={{
        display: "inline-flex", alignItems: "center", gap: 0.5, maxWidth: "100%",
        borderRadius: "6px", border: "1px solid", borderColor: "divider",
        color: "text.secondary",
        px: 1, py: 0.375,
        fontSize: "0.75rem", fontWeight: 600,
      }}
    >
      {icon && <Box sx={ixBox(12)}><Cpu size="100%" color="currentColor" variant="Linear" /></Box>}
      <Box component="span" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</Box>
    </Box>
  );
}

export default function KnowledgeSourcesTab({ workspaceId, sources, loading, onRefresh, agents = [] }) {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [mode, setMode] = useState("file");
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [faqEntries, setFaqEntries] = useState([{ q: "", a: "" }]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedSource, setSelectedSource] = useState(null);
  const [viewContent, setViewContent] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Poll while any source is still processing
  const hasProcessing = sources.some((s) => s.status === "processing" && s.status !== "failed");
  useEffect(() => {
    if (!hasProcessing) return;
    const id = setInterval(() => onRefresh(), 3000);
    return () => clearInterval(id);
  }, [hasProcessing, onRefresh]);

  // Keep drawer in sync when sources refresh
  useEffect(() => {
    if (!selectedSource) return;
    const updated = sources.find((s) => s._id === selectedSource._id);
    if (updated) setSelectedSource(updated);
  }, [sources, selectedSource]);

  const resetDialog = () => {
    setMode("file"); setFile(null); setTitle(""); setFaqEntries([{ q: "", a: "" }]);
  };
  const openDialog = () => { resetDialog(); setUploadOpen(true); };

  const handleUpload = async () => {
    setUploading(true);
    try {
      const token = await getAuthToken();
      const url = `${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/knowledge-sources`;
      let res;
      if (mode === "faq") {
        const valid = faqEntries.filter((e) => e.q.trim() && e.a.trim());
        if (!valid.length) { toast.error("Add at least one Q&A pair."); setUploading(false); return; }
        res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ type: "faq", title: title.trim() || "FAQ", entries: valid }),
        });
      } else {
        if (!file) { toast.error("Select a file first."); setUploading(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        if (title.trim()) fd.append("title", title.trim());
        res = await fetch(url, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      }
      const data = await res.json();
      if (!res.ok) { toast.error(data?.message || "Upload failed."); return; }
      setUploadOpen(false); onRefresh();
    } catch { toast.error("Upload failed. Check server connection."); }
    finally { setUploading(false); }
  };

  const handleViewDocument = async (sourceId) => {
    setViewLoading(true);
    try {
      const token = await getAuthToken();
      const res = await fetch(`${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/knowledge-sources/${sourceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setViewContent(data?.source?.sourceText || "No content available.");
    } catch {
      setViewContent("Failed to load document content.");
    } finally {
      setViewLoading(false);
    }
  };

  // Was a bare `window.confirm()` — the raw browser "localhost:3000 says"
  // dialog, the one popup on this page that didn't match anything else
  // (Rahim, 2026-09-04: "fix the delete pop up right now it uses this
  // weird one"). Split into requestDelete (opens the styled dialog below)
  // + confirmDelete (the actual DELETE call, now fired from that dialog's
  // own Delete button) — same request/response handling as before, just
  // no longer gated by the native confirm().
  const requestDelete = (source) => setDeleteTarget(source);
  const closeDeleteDialog = () => setDeleteTarget(null);

  const handleDelete = async (sourceId) => {
    setDeleting(true);
    try {
      const token = await getAuthToken();
      await fetch(`${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/knowledge-sources/${sourceId}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedSource(null);
      setDeleteTarget(null);
      onRefresh();
    } catch (err) { console.error("Failed to delete source", err); }
    finally { setDeleting(false); }
  };

  const updateFaqEntry = (i, field, value) =>
    setFaqEntries((prev) => prev.map((e, idx) => (idx === i ? { ...e, [field]: value } : e)));
  const addFaqRow = () => setFaqEntries((prev) => [...prev, { q: "", a: "" }]);
  const removeFaqRow = (i) => setFaqEntries((prev) => prev.filter((_, idx) => idx !== i));

  const agentsBySourceId = useMemo(() => {
    const map = {};
    agents.forEach((agent) => {
      (agent.knowledgeSourceIds || []).forEach((sid) => {
        const key = String(sid);
        if (!map[key]) map[key] = [];
        map[key].push(agent.name || "Unnamed Agent");
      });
    });
    return map;
  }, [agents]);

  const uniqueTypes = useMemo(() => [...new Set(sources.map((s) => s.type))], [sources]);

  const filteredSources = useMemo(() => {
    return sources.filter((src) => {
      const matchSearch = !search || (src.title || "").toLowerCase().includes(search.toLowerCase());
      const matchType = typeFilter === "all" || src.type === typeFilter;
      return matchSearch && matchType;
    });
  }, [sources, search, typeFilter]);

  return (
    <Box>
      {/* Own "Knowledge Sources" title + subtitle removed (Rahim,
          2026-09-04) — redundant now that the page's top PanelHeading
          itself reads "Knowledge Sources" on this tab. Search + type
          filter moved up onto this same row, aligned with Add Source
          (Rahim, 2026-09-04), instead of sitting on their own row below. */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.5, mb: 2.5 }}>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", flex: 1, minWidth: 0 }}>
          <TextField
            size="small"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={ixBox(16)}><SearchNormal1 size="100%" color="currentColor" variant="Linear" /></Box>
                </InputAdornment>
              ),
            }}
            sx={{ ...inboxInputSx, flex: 1, maxWidth: 320 }}
          />
          <Select
            size="small"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            MenuProps={inboxSelectMenuProps}
            sx={{ ...inboxSelectSx, minWidth: 180, fontSize: "0.875rem" }}
          >
            <MenuItem value="all" sx={inboxMenuItemSx}>All Types</MenuItem>
            {uniqueTypes.map((t) => (
              <MenuItem key={t} value={t} sx={inboxMenuItemSx}>{SOURCE_TYPE_LABELS[t] || t}</MenuItem>
            ))}
          </Select>
        </Box>
        <Button
          variant="outlined"
          onClick={openDialog}
          disabled={!workspaceId}
          startIcon={<Box sx={ixBox(16)}><DocumentUpload size="100%" color="currentColor" variant="Linear" /></Box>}
          sx={{ ...neutralButtonSx, fontSize: "0.8125rem", flexShrink: 0 }}
        >
          Add Source
        </Button>
      </Box>

      <Box
        sx={{
          border: "1px solid",
          borderColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.12)"),
          borderRadius: "12px",
          overflow: "hidden",
          bgcolor: "background.paper",
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Name", "Type", "Agents", "Created At", "Status"].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      fontWeight: 600, fontSize: "0.8125rem", color: "text.secondary",
                      border: 0, borderBottom: "1px solid",
                      borderColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"),
                      bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)"),
                      py: 1.25,
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell sx={cellSx}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Skeleton variant="circular" width={18} height={18} sx={skeletonSx} />
                      <Skeleton variant="text" width={140} sx={{ ...skeletonSx, fontSize: "0.9375rem" }} />
                    </Box>
                  </TableCell>
                  <TableCell sx={cellSx}><Skeleton variant="text" width={60} sx={{ ...skeletonSx, fontSize: "0.8125rem" }} /></TableCell>
                  <TableCell sx={cellSx}><Skeleton variant="rounded" width={70} height={22} sx={{ ...skeletonSx, borderRadius: "6px" }} /></TableCell>
                  <TableCell sx={cellSx}><Skeleton variant="text" width={90} sx={{ ...skeletonSx, fontSize: "0.8125rem" }} /></TableCell>
                  <TableCell sx={cellSx}><Skeleton variant="rounded" width={70} height={22} sx={{ ...skeletonSx, borderRadius: "6px" }} /></TableCell>
                </TableRow>
              ))}
              {!loading && filteredSources.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ ...cellSx, border: 0 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 5, gap: 1 }}>
                      <Box
                        sx={{
                          width: 44, height: 44, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center",
                          bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"),
                        }}
                      >
                        <Box sx={ixBox(22)}><DocumentText size="100%" color="currentColor" variant="Linear" /></Box>
                      </Box>
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "text.primary" }}>
                        {sources.length === 0 ? "No knowledge sources yet" : "No results found"}
                      </Typography>
                      <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", textAlign: "center", maxWidth: 280 }}>
                        {sources.length === 0
                          ? "Upload a PDF, DOCX, TXT, or add a FAQ for your agents to reference."
                          : "Try a different search term or type filter."}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {filteredSources.map((src) => {
                const assignedAgents = agentsBySourceId[String(src._id)] || [];
                return (
                  <TableRow
                    key={src._id}
                    hover
                    onClick={() => setSelectedSource(src)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell sx={cellSx}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={ixBox(18)}><DocumentText size="100%" color="currentColor" variant="Linear" /></Box>
                        <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "text.primary" }}>{src.title}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
                        {SOURCE_TYPE_LABELS[src.type] || src.type}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      {assignedAgents.length > 0 ? (
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {assignedAgents.slice(0, 2).map((name, i) => (
                            <AgentTag key={i} label={name} />
                          ))}
                          {assignedAgents.length > 2 && (
                            <AgentTag label={`+${assignedAgents.length - 2}`} icon={false} />
                          )}
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: "0.8125rem", color: "text.disabled" }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>{new Date(src.createdAt).toLocaleDateString()}</Typography>
                    </TableCell>
                    <TableCell sx={cellSx}><StatusChip status={src.status} /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Right-side detail panel */}
      <Drawer
        anchor="right"
        open={!!selectedSource}
        onClose={() => setSelectedSource(null)}
        ModalProps={{ BackdropProps: { invisible: true } }}
        PaperProps={{
          sx: {
            width: 360,
            boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
            bgcolor: "background.paper",
          },
        }}
      >
        {selectedSource && (
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            {/* Height pinned to PanelHeading.jsx's own exact breakpoint
                values (Rahim, 2026-09-04: "the top headers bottom border
                line is not matching") — this Box's border-bottom sits at a
                different Y than the page's own top bar because its height
                was intrinsic (padding + content) instead of matching that
                fixed height; boxSizing:"border-box" needed since this app
                has no global border-box reset (see ui-gotchas memory). */}
            <Box sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              // A couple px taller than PanelHeading's own values (Rahim:
              // "a little more to the bottom" then "too much now a little
              // up") — split the difference between PanelHeading's exact
              // numbers and the first +4px nudge.
              height: { xs: "51px", sm: "63px", md: "65px", lg: "65px", xl: "65px" },
              boxSizing: "border-box",
              px: 2.5, borderBottom: "1px solid", borderColor: "divider",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, mr: 1, overflow: "hidden" }}>
                <Box sx={ixBox(20)}><DocumentText size="100%" color="currentColor" variant="Linear" /></Box>
                <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "text.primary" }} noWrap>{selectedSource.title}</Typography>
              </Box>
              <IconButton size="small" onClick={() => setSelectedSource(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Stack spacing 2.5->3 + LabeledField gap 0.75->1 (Rahim,
                2026-09-04: "give proper spacing") — with a divider between
                every row, Stack's default 20px split 10px/10px above/below
                each Divider read cramped against the label-to-value gap
                right next to it. */}
            <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 3 }}>
              <Stack spacing={3} divider={<Divider />}>
                <LabeledField label="Status" sx={{ gap: 1 }}>
                  <Box><StatusChip status={selectedSource.status} /></Box>
                </LabeledField>

                <LabeledField label="Type" sx={{ gap: 1 }}>
                  <Typography sx={{ fontSize: "0.875rem", color: "text.primary" }}>
                    {SOURCE_TYPE_LABELS[selectedSource.type] || selectedSource.type}
                  </Typography>
                </LabeledField>

                <LabeledField label="Created" sx={{ gap: 1 }}>
                  {/* Was one dense "7/10/2026, 5:43:04 PM" string (Rahim,
                      2026-09-04: "make ui better for this"). Split into a
                      readable date (matching AgentCard's own createdAt
                      format exactly) with the time as a smaller, muted
                      second line — the extra precision stays available
                      without competing with the date for attention. */}
                  <Box>
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.primary" }}>
                      {new Date(selectedSource.createdAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.125 }}>
                      {new Date(selectedSource.createdAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                    </Typography>
                  </Box>
                </LabeledField>

                <LabeledField label="Assigned To" sx={{ gap: 1 }}>
                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                    {(agentsBySourceId[String(selectedSource._id)] || []).length > 0
                      ? (agentsBySourceId[String(selectedSource._id)] || []).map((name, i) => (
                          <AgentTag key={i} label={name} />
                        ))
                      : <Typography sx={{ fontSize: "0.875rem", color: "text.disabled" }}>No agents assigned</Typography>
                    }
                  </Box>
                </LabeledField>
              </Stack>
            </Box>

            <Box sx={{ px: 2.5, py: 2, borderTop: "1px solid", borderColor: "divider", display: "flex", flexDirection: "column", gap: 1 }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={viewLoading ? <CircularProgress size={14} /> : <Box sx={ixBox(16)}><ExportSquare size="100%" color="currentColor" variant="Linear" /></Box>}
                disabled={viewLoading || selectedSource.status === "processing"}
                onClick={() => handleViewDocument(selectedSource._id)}
                sx={{ ...neutralButtonSx, fontSize: "0.8125rem" }}
              >
                View Document
              </Button>
              <Button
                variant="outlined"
                color="error"
                fullWidth
                startIcon={<Box sx={ixBox(16)}><Trash size="100%" color="currentColor" variant="Linear" /></Box>}
                onClick={() => requestDelete(selectedSource)}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: "0.8125rem" }}
              >
                Delete
              </Button>
            </Box>
          </Box>
        )}
      </Drawer>

      {/* View document dialog */}
      <Dialog open={!!viewContent} onClose={() => setViewContent(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
        <DialogTitle sx={{ borderBottom: "1px solid", borderColor: "divider", py: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "text.primary" }}>{selectedSource?.title}</Typography>
            <IconButton size="small" onClick={() => setViewContent(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 2.5 }}>
          <Typography
            component="pre"
            sx={{ fontSize: "0.875rem", color: "text.primary", whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "inherit", m: 0, lineHeight: 1.6 }}
          >
            {viewContent}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setViewContent(null)} variant="outlined" sx={{ ...neutralButtonSx, fontSize: "0.8125rem" }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onClose={() => !uploading && setUploadOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "1rem", fontWeight: 600, color: "text.primary", borderBottom: "1px solid", borderColor: "divider", px: 3, py: 2.25 }}>
          Add Knowledge Source
          {/* Cross button, top-right (Rahim, 2026-09-04) — this dialog had
              no close affordance besides the footer Cancel button. Same
              plain CloseIcon as every other close button on this page. */}
          <IconButton size="small" onClick={() => !uploading && setUploadOpen(false)} disabled={uploading}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        {uploading && <LinearProgress />}
        {/* Roomier spacing throughout (Rahim, 2026-09-04: "use better
            spacing" — the File/FAQ row was "literally touching the top
            border"). Root cause: MUI's own DialogContent hardcodes
            `.MuiDialogTitle-root + & { paddingTop: 0 }` whenever `dividers`
            isn't set — a sibling-selector rule with higher CSS specificity
            than our sx `pt` override, so the pt was silently losing. Fixed
            by moving the top padding onto an inner Box instead (not a
            direct sibling of DialogTitle, so the override doesn't apply to
            it) rather than fighting MUI's own rule. */}
        <DialogContent sx={{ px: 3, pt: 0, pb: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {/* Explainer above the File/FAQ picker (Rahim, 2026-09-04) —
                  neither option's own label makes clear WHY you'd pick one
                  over the other. */}
              <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
                Upload a document for your agent to search through, or write out a list of
                questions and answers it can match against directly.
              </Typography>
              <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
                <ButtonBase onClick={() => setMode("file")} sx={segmentedOptionSx(mode === "file")}>
                  File (PDF / DOCX / TXT / Excel)
                </ButtonBase>
                <ButtonBase onClick={() => setMode("faq")} sx={segmentedOptionSx(mode === "faq")}>
                  FAQ (Q&amp;A pairs)
                </ButtonBase>
              </Box>
            </Box>

            <LabeledField label="Title (optional)">
            <TextField
              value={title} onChange={(e) => setTitle(e.target.value)}
              size="small" fullWidth placeholder={mode === "faq" ? "My FAQ" : "Leave blank to use filename"}
              sx={inboxInputSx}
            />
          </LabeledField>

          {mode === "file" && (
            <Box>
              <input ref={fileRef} type="file" accept={ACCEPTED_MIME} style={{ display: "none" }} onChange={(e) => setFile(e.target.files[0] || null)} />
              {file ? (
                // A picked file was rendering inside the exact same neutral
                // "Choose file…" button, just with its label swapped for the
                // filename (Rahim, 2026-09-04: "should tell something is
                // already added... the button is same colour"). Now a
                // distinct card — filename, size, and its own remove (x) so
                // re-picking doesn't require reopening the file browser.
                // Kept neutral grey/black (Rahim: "no need to make it
                // green keep it simple grey colour or black"), not a
                // success-green tint.
                <Box
                  sx={{
                    display: "flex", alignItems: "center", gap: 1,
                    borderRadius: "8px", border: "1px solid",
                    borderColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.23)" : theme.palette.divider),
                    bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)"),
                    px: 1.25, py: 1,
                  }}
                >
                  <Box sx={{ ...ixBox(18), color: "text.secondary" }}>
                    <DocumentText size="100%" color="currentColor" variant="Bold" />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "text.primary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {file.name}
                    </Typography>
                    <Typography sx={{ fontSize: "0.6875rem", color: "text.secondary" }}>{formatFileSize(file.size)}</Typography>
                  </Box>
                  <IconButton size="small" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.25 }}>
                  <Button
                    variant="outlined"
                    onClick={() => fileRef.current?.click()}
                    startIcon={<Box sx={ixBox(16)}><DocumentUpload size="100%" color="currentColor" variant="Linear" /></Box>}
                    sx={{ ...neutralButtonSx, fontSize: "0.8125rem" }}
                  >
                    Choose file…
                  </Button>
                  <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>Max 20 MB</Typography>
                </Box>
              )}
            </Box>
          )}

          {mode === "faq" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {faqEntries.map((entry, i) => (
                <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.25 }}>
                    <LabeledField label={`Question ${i + 1}`}>
                      <TextField value={entry.q} onChange={(e) => updateFaqEntry(i, "q", e.target.value)} size="small" fullWidth placeholder="Question" sx={inboxInputSx} />
                    </LabeledField>
                    <ExpandableParagraphField
                      label={`Answer ${i + 1}`}
                      placeholder="Answer"
                      value={entry.a}
                      onChange={(v) => updateFaqEntry(i, "a", v)}
                    />
                  </Box>
                  <IconButton size="small" onClick={() => removeFaqRow(i)} sx={{ mt: 3.5 }}>
                    <Box sx={ixBox(16)}><Trash size="100%" color="currentColor" variant="Linear" /></Box>
                  </IconButton>
                </Box>
              ))}
              <Button
                onClick={addFaqRow}
                size="small"
                variant="outlined"
                startIcon={<Box sx={ixBox(14)}><AddCircle size="100%" color="currentColor" variant="Linear" /></Box>}
                sx={{ ...neutralButtonSx, fontSize: "0.8125rem", alignSelf: "flex-start" }}
              >
                Add Q&amp;A pair
              </Button>
            </Box>
            )}
          </Box>
        </DialogContent>
        {/* Cancel demoted to plain text + moved to the left (Rahim,
            2026-09-04: "for cancel use simple text and add it to the left
            side of the card") — space-between splits it from Upload, which
            stays the one real bordered action on the right. */}
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider", justifyContent: "space-between" }}>
          <Button
            onClick={() => setUploadOpen(false)}
            disabled={uploading}
            variant="text"
            sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8125rem", color: (theme) => theme.palette.text.secondary }}
          >
            Cancel
          </Button>
          <Button variant="outlined" onClick={handleUpload} disabled={uploading} sx={{ ...neutralButtonSx, fontSize: "0.8125rem" }}>
            {uploading ? "Uploading…" : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation — was a bare window.confirm() (the raw
          browser "localhost:3000 says" dialog, Rahim 2026-09-04: "fix the
          delete pop up right now it uses this weird one"). Rebuilt to
          match Inbox's own delete-confirm dialog exactly (MessageCard.jsx's
          confirmDialog) — title+close row, body copy in an inner Box (same
          DialogTitle+DialogContent padding-collapse fix used on the Upload
          dialog above), Cancel/Delete split via space-between. */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && closeDeleteDialog()}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "1rem", fontWeight: 600, color: "text.primary", borderBottom: "1px solid", borderColor: "divider", px: 3, py: 2.25 }}>
          Delete Knowledge Source
          <IconButton size="small" onClick={closeDeleteDialog} disabled={deleting}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 0, pb: 3 }}>
          <Box sx={{ pt: 3 }}>
            <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
              Are you sure you want to delete <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>{deleteTarget?.title}</Box>?
              This can't be undone, and any agent using it will lose access to this source.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider", justifyContent: "space-between" }}>
          <Button
            onClick={closeDeleteDialog}
            disabled={deleting}
            variant="text"
            sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8125rem", color: (theme) => theme.palette.text.secondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDelete(deleteTarget._id)}
            disabled={deleting}
            color="error"
            variant="contained"
            disableElevation
            autoFocus
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: "0.8125rem" }}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
